import { create } from 'zustand'
import { IProductImage } from '@/types'
import type { StockCheckCalculatedStock } from '@/services/api'
import { mergeCalculatedStock } from '@/utils/stockUtils'

interface IDisplay {
	configuratorFrontOn: boolean
	'z-index'?: number
}

interface IComponentVariantId {
	$oid: string
}

export interface IMaterialInformation {
	materialId: string
	kibu_name: string
	finish: string
	hex_code: string
}

export interface IComponent {
	componentVariantId: IComponentVariantId
	position?: string
	materialOptions?: string[]
	defaultMaterial?: string
	display?: IDisplay[]
	name: string
	componentType: string
}

export interface ISaleLimit {
	type: string
	maxQuantity?: number
	period?: number
	unit?: string
	startDate?: string
	endDate?: string
}

export interface IColourway {
	_id: string
	productId: string
	name: string
	description: string
	stripeProductId: string | null
	isAvailableForSale: boolean
	saleLimit?: ISaleLimit | null
	title: string
	createdAt: string
	price?: IPrice
}

export interface IPrice {
	price_id: string
	unit_amount: number
	currency: string
}

export type StockStatus =
	| 'IN_STOCK'
	| 'LOW_STOCK'
	| 'PREORDER'
	| 'OUT_OF_STOCK'
	| 'COMING_SOON'
	| 'BACK_SOON'
	| 'NOT_YET_AVAILABLE'
	| 'NO_LONGER_AVAILABLE'
	| 'INSUFFICIENT_STOCK'
	| 'EXCEEDS_MAX_PER_ORDER'

export interface IStockTimeFrame {
	hasStarted: boolean
	hasEnded: boolean
	startDate?: string
	endDate?: string
	daysRemaining?: number
	daysUntilStart?: number
}

export interface IEstimatedDispatchDays {
	min?: number
	max?: number
	unit?: string
}

export interface IStockLimits {
	stockStatus?: StockStatus
	stockMessage?: string | null
	isPreorder?: boolean | null
	canPreorder?: boolean | null
	outOfStockSince?: string | null
	availableForSale?: boolean | null
	remainingStock?: number | null
	timeFrame?: IStockTimeFrame | null
	maxPerOrder?: number | null
	maxDelayDays?: number | null
	additionalDispatchDays?: number | null
	estimatedDispatchDays?: IEstimatedDispatchDays | null
	showBadge?: boolean
	globalStockRemaining?: number
	rollingWindowRemaining?: number
	hasRollingWindowStock?: boolean
	sharedPool?: {
		totalAvailable?: number | null
		siblingProductItemIds?: string[]
	} | null
}

export interface IPartsModifications {
	[key: string]: {
		type: string
		modifiedPartId: string[]
		maxLength?: number
		description?: string
	};
};

/** Render layers for the custom colours configurator; only sent when every layer exists. */
export interface IConfiguratorRenders {
	layers: Record<string, string> // "variant|position|material" -> image URL
	masks: Record<string, string> // "variant|position" -> click mask (PNG data URL)
	anchors?: Record<string, [number, number] | null> // "variant|position" -> a point on the part, for tap markers
	bounds: [number, number, number, number] | null // headphones' box in the frame, as fractions
}

export interface IProduct {
	_id: string
	productName: string
	productTitle: string
	description: string
	productCatalogueId: string
	optionTitle?: string
	imageUrl?: string
	heroImage?: IProductImage | null
	components?: ICustomComponent[]
	configuratorRenders?: IConfiguratorRenders
	price?: {
		price_id: string
		unit_amount: number
		currency: string
	}
	availableOptions?: IOption[]
	componentModifications?: {
		engravingText?: {
			type: string
			maxLength: number
			description: string
			componentType: string
			processType: string
		}
	}
	partsModifications?: {
		[key: string]: {
			type: string
			modifiedPartId: string[]
			maxLength?: number
			description?: string
		}
	}
	productDescription?: {
		mobile: string
		desktop: string
	}
	averageCatalogueRating?: number
	totalCatalogueReviews?: number
	rank?: number,
	group?: string
	stockLimits?: IStockLimits
}

export interface IOption {
	productCatalogueId: string
	limit: number
	order: number
	title: string
	required?: boolean
	options: IProduct[]
	info?: IInfoDetails
}

export interface ICustomComponent {
	componentVariantId: { $oid: string }
	componentType: string
	name: string
	position: string | null
	chosenMaterial: { $oid: string }
	display?: Array<{
		configuratorFrontOn: boolean
		'z-index'?: number
	}>
	materialOptions?: string[]
	defaultMaterial?: string
}

interface IProductDataStore {
	product: IProduct | null
	options: IOption[]
	materialInformation: IMaterialInformation[]
	setProductData: (product: IProduct, addons: IOption[], materialInfo: IMaterialInformation[]) => void
	applyCalculatedStock: (stock: ReadonlyMap<string, StockCheckCalculatedStock | undefined>) => void
}

export interface IInfoDetails {
	title: string,
	text: string
}

const useProductDataStore = create<IProductDataStore>((set) => ({
	product: null,
	options: [],
	materialInformation: [],
	setProductData: (product, options, materialInfo) => set({ product, options, materialInformation: materialInfo }),
	// Delayed stock responses must update today's catalogue, never restore the
	// product/options snapshot captured when their request began.
	applyCalculatedStock: (stock) => set(state => ({
		product: state.product ? mergeCalculatedStock(state.product, stock.get(String(state.product._id))) : null,
		options: state.options.map(group => ({ ...group, options: group.options.map(item => mergeCalculatedStock(item, stock.get(String(item._id)))) })),
	})),
}))

export default useProductDataStore
